import { startOfDay } from "date-fns";
import type { RecapMode, RangeMode } from "../types";
import {
  NO_EMPLOYEE_FILTER_ID,
  NO_LOCATION_FILTER_ID,
  type FilterState,
} from "../types/filter.types";
import {
  ALL_STATUS_KEYS,
  type StatusFilterKey,
  type StatusSelection,
} from "../config/statusFilters";
import { extractZonedDateStr } from "@/lib/datetime/companyTimezone";

export interface RecapStateView {
  recapMode: RecapMode;
  rangeMode: RangeMode;
  selectedDate: Date;
  endDate: string | undefined;
  startDateTime: string | undefined;
  endDateTime: string | undefined;
  statusFilter: StatusSelection;
  searchQuery: string;
  filters: FilterState;
  showDeletedOnly: boolean;
}

export interface PreOrderTarget {
  id: string;
  partnerId: string | null;
  partnerName: string;
  /** ISO datetime, e.g. "2026-04-30T12:34:56" — may be null for unsaved/edge data. */
  orderDate: string | null;
  /** YYYY-MM-DD — may be null. */
  deliveryDate: string | null;
  /** Numeric status (0=pending, 1=confirmed, 2=exported, 6=cancelled) — null for unknown. */
  status: number | null;
  /**
   * Export-failed and AI-proposal buckets aren't derivable from the numeric
   * status alone (the first is metadata-driven, the second spans status 8 +
   * pending suggestions). These flags let the status-pill widening reach those
   * rows; default false keeps older target literals valid.
   */
  isExportFailed?: boolean;
  hasAiChanges?: boolean;
  sourceType: number | null;
  employeeId: string | null;
  locationId: string | null;
  isDeleted: boolean;
}

export interface VisibilityPatch {
  rangeMode?: RangeMode;
  selectedDate?: Date;
  endDate?: string;
  startDateTime?: string;
  endDateTime?: string;
  statusFilter?: StatusSelection;
  searchQuery?: string;
  filters?: FilterState;
  showDeletedOnly?: boolean;
}

export interface VisibilityComputeContext {
  userPartnerIds: Set<string> | null;
}

const STATUS_TO_TAB: Record<number, StatusFilterKey> = {
  0: "pending",
  1: "confirmed",
  2: "exported",
  6: "cancelled",
  8: "ai_created",
};

const MAX_RANGE_EXPANSION_DAYS = 2;

function dayDiff(aIso: string, bIso: string): number {
  const [yA, mA, dA] = aIso.split("-").map(Number);
  const [yB, mB, dB] = bIso.split("-").map(Number);
  return (Date.UTC(yA, mA - 1, dA) - Date.UTC(yB, mB - 1, dB)) / 86400000;
}

/**
 * Extract the calendar day (YYYY-MM-DD) of an API datetime string IN THE
 * COMPANY TIMEZONE.
 *
 * The naive `iso.slice(0, 10)` we used to call here picks the UTC day,
 * which day-shifts whenever the order's wall-clock time in the company
 * tz puts it on a different calendar day than its UTC components. Most
 * visible case: an order at 00:00 in a positive-offset tz (Paris UTC+2
 * → order shows "May 18 02:00 Paris", stored as "May 18 00:00 UTC") is
 * fine, BUT an order at "May 18 00:00 in the company tz" arrives as
 * "May 17 22:00 UTC" — and a UTC-prefix `.slice(0,10)` resolves to
 * May 17, snapping the recap to the previous day after an AI accept
 * even though the order itself stays on May 18.
 *
 * `extractZonedDateStr` from the canonical datetime layer handles both
 * date-only ("2026-05-18") and datetime ("2026-05-18T22:00:00") inputs,
 * the latter being tz-converted before the day is read.
 */
function dayOnly(iso: string | null, tz: string): string | null {
  return extractZonedDateStr(iso, tz);
}

function formatDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface DateWindowCheck {
  inside: boolean;
  /** When false, suggested patch to extend or relocate the window. */
  patch: VisibilityPatch | null;
}

function pickCandidateDay(
  recapMode: RecapMode,
  orderDay: string | null,
  deliveryDay: string | null,
): string | null {
  if (recapMode === "order") return orderDay;
  if (recapMode === "delivery") return deliveryDay;
  return deliveryDay ?? orderDay;
}

function exactSnapPatch(candidate: string): VisibilityPatch {
  return {
    rangeMode: "exact",
    selectedDate: startOfDay(new Date(candidate + "T00:00:00")),
    endDate: undefined,
    startDateTime: undefined,
    endDateTime: undefined,
  };
}

function checkExactMode(
  recapMode: RecapMode,
  candidate: string,
  selectedDay: string,
  orderDay: string | null,
  deliveryDay: string | null,
): DateWindowCheck {
  const inside =
    recapMode === "all"
      ? orderDay === selectedDay || deliveryDay === selectedDay
      : candidate === selectedDay;
  if (inside) return { inside: true, patch: null };
  return { inside: false, patch: exactSnapPatch(candidate) };
}

function isInsideRangeWindow(
  recapMode: RecapMode,
  candidate: string,
  orderDay: string | null,
  deliveryDay: string | null,
  selectedDay: string,
  winEnd: string,
): boolean {
  const fits = (d: string | null) =>
    d != null && d >= selectedDay && d <= winEnd;
  return recapMode === "all"
    ? fits(orderDay) || fits(deliveryDay)
    : fits(candidate);
}

function checkRangeMode(
  recapMode: RecapMode,
  candidate: string,
  selectedDay: string,
  endDay: string | null,
  orderDay: string | null,
  deliveryDay: string | null,
): DateWindowCheck {
  const winEnd = endDay ?? selectedDay;
  if (
    isInsideRangeWindow(
      recapMode,
      candidate,
      orderDay,
      deliveryDay,
      selectedDay,
      winEnd,
    )
  ) {
    return { inside: true, patch: null };
  }
  if (candidate < selectedDay) {
    const delta = dayDiff(selectedDay, candidate);
    if (delta > MAX_RANGE_EXPANSION_DAYS) {
      return { inside: false, patch: exactSnapPatch(candidate) };
    }
    return {
      inside: false,
      patch: { selectedDate: startOfDay(new Date(candidate + "T00:00:00")) },
    };
  }
  const delta = dayDiff(candidate, winEnd);
  if (delta > MAX_RANGE_EXPANSION_DAYS) {
    return { inside: false, patch: exactSnapPatch(candidate) };
  }
  return { inside: false, patch: { endDate: candidate } };
}

function checkStartingFromMode(
  recapMode: RecapMode,
  candidate: string,
  selectedDay: string,
  orderDay: string | null,
  deliveryDay: string | null,
): DateWindowCheck {
  const fitsFrom = (d: string | null) => d != null && d >= selectedDay;
  const inside =
    recapMode === "all"
      ? fitsFrom(orderDay) || fitsFrom(deliveryDay)
      : fitsFrom(candidate);
  if (inside) return { inside: true, patch: null };
  if (
    candidate < selectedDay &&
    dayDiff(selectedDay, candidate) > MAX_RANGE_EXPANSION_DAYS
  ) {
    return { inside: false, patch: exactSnapPatch(candidate) };
  }
  return {
    inside: false,
    patch: { selectedDate: startOfDay(new Date(candidate + "T00:00:00")) },
  };
}

function checkDateDimensionFor(
  current: RecapStateView,
  target: PreOrderTarget,
  tz: string,
): DateWindowCheck {
  // Both extracted in the company tz so they line up with `selectedDay`
  // below (which derives from `current.selectedDate`, a Date whose
  // browser-local fields already represent the company-tz day —
  // `parseApiDateToLocalDate` is the convention on the picker side).
  // `delivery_date` is usually a DATE column (no time) so the tz pass
  // is a no-op for it; we still route through `dayOnly` to handle the
  // rare case where it carries a time component (legacy data paths).
  const orderDay = dayOnly(target.orderDate, tz);
  const deliveryDay = dayOnly(target.deliveryDate, tz);

  const candidate = pickCandidateDay(current.recapMode, orderDay, deliveryDay);
  if (!candidate) return { inside: true, patch: null };

  const selectedDay = formatDay(startOfDay(current.selectedDate));
  const endDay = current.endDate ?? null;

  if (current.rangeMode === "exact") {
    return checkExactMode(
      current.recapMode,
      candidate,
      selectedDay,
      orderDay,
      deliveryDay,
    );
  }
  if (current.rangeMode === "range") {
    return checkRangeMode(
      current.recapMode,
      candidate,
      selectedDay,
      endDay,
      orderDay,
      deliveryDay,
    );
  }
  return checkStartingFromMode(
    current.recapMode,
    candidate,
    selectedDay,
    orderDay,
    deliveryDay,
  );
}

/**
 * Resolve the status buckets a target belongs to, mirroring the predicate
 * priority in `STATUS_FILTER_DEFS`: export-failed is exclusive; otherwise the
 * numeric-status bucket and the AI-proposal bucket can both apply.
 */
function targetStatusKeys(target: PreOrderTarget): StatusFilterKey[] {
  if (target.isExportFailed) return ["export_failed"];
  const keys: StatusFilterKey[] = [];
  const numeric =
    target.status == null ? undefined : STATUS_TO_TAB[target.status];
  if (numeric) keys.push(numeric);
  // AI-created targets already resolved to "ai_created" via the numeric map;
  // an AI signal on any other status means "ai_modified".
  if (target.hasAiChanges && target.status !== 8) keys.push("ai_modified");
  return keys;
}

function statusInActiveTab(
  selection: StatusSelection,
  target: PreOrderTarget,
): boolean {
  // `[]` shows nothing → any target is hidden and the patch widens back to all.
  if (selection.length === 0) return false;
  const keys = targetStatusKeys(target);
  // Genuinely unknown status (no bucket resolved) → don't treat the row as
  // hidden by a status pill; there's nothing meaningful to widen toward.
  if (keys.length === 0) return true;
  return keys.some((key) => selection.includes(key));
}

/**
 * Compute the minimal set of state changes needed so the target pre-order
 * becomes visible in the recap view.
 *
 * Pure: no React. Each dimension is evaluated independently and only
 * mutated if it would currently hide the target.
 *
 * The recapMode dimension is evaluated against `current.recapMode` as-is.
 * Callers that want to land in a specific mode (e.g. delivery-banner UX)
 * pass that mode in `current.recapMode` and apply `setRecapMode` themselves.
 */
interface FiltersResult {
  next: FilterState;
  changed: boolean;
}

function applySourcesFilter(
  current: FilterState,
  target: PreOrderTarget,
  result: FiltersResult,
): void {
  if (
    current.sources.length === 0 ||
    target.sourceType == null ||
    current.sources.includes(target.sourceType)
  ) {
    return;
  }
  result.next.sources = [...current.sources, target.sourceType];
  result.changed = true;
}

function applyEmployeesFilter(
  current: FilterState,
  target: PreOrderTarget,
  result: FiltersResult,
): void {
  if (current.employees.length === 0) return;
  if (!target.employeeId) {
    if (current.employees.includes(NO_EMPLOYEE_FILTER_ID)) return;
    result.next.employees = [];
    result.changed = true;
    return;
  }
  if (!current.employees.includes(target.employeeId)) {
    result.next.employees = [...current.employees, target.employeeId];
    result.changed = true;
  }
}

function applyLocationsFilter(
  current: FilterState,
  target: PreOrderTarget,
  result: FiltersResult,
): void {
  if (current.locations.length === 0) return;
  if (!target.locationId) {
    if (current.locations.includes(NO_LOCATION_FILTER_ID)) return;
    result.next.locations = [];
    result.changed = true;
    return;
  }
  if (!current.locations.includes(target.locationId)) {
    result.next.locations = [...current.locations, target.locationId];
    result.changed = true;
  }
}

function applyMyPartnersFilter(
  current: FilterState,
  target: PreOrderTarget,
  ctx: VisibilityComputeContext,
  result: FiltersResult,
): void {
  if (!current.myPartnersOnly) return;
  const partnerIds = ctx.userPartnerIds;
  const isMine =
    target.partnerId && partnerIds && partnerIds.size > 0
      ? partnerIds.has(target.partnerId)
      : false;
  if (isMine) return;
  result.next.myPartnersOnly = false;
  result.changed = true;
}

function applyClientGroupsFilter(
  current: FilterState,
  result: FiltersResult,
): void {
  // We don't carry the target's client_group_ids on PreOrderTarget (callers
  // typically only know partner_id), so always clear this filter when set —
  // the row is then guaranteed visible regardless of group membership.
  if (!current.clientGroupIds || current.clientGroupIds.length === 0) return;
  result.next.clientGroupIds = [];
  result.changed = true;
}

function computeFiltersPatch(
  current: FilterState,
  target: PreOrderTarget,
  ctx: VisibilityComputeContext,
): FiltersResult {
  const result: FiltersResult = {
    next: { ...current },
    changed: false,
  };
  applySourcesFilter(current, target, result);
  applyEmployeesFilter(current, target, result);
  applyLocationsFilter(current, target, result);
  applyMyPartnersFilter(current, target, ctx, result);
  applyClientGroupsFilter(current, result);
  return result;
}

export function computeVisibilityPatch(
  current: RecapStateView,
  target: PreOrderTarget,
  ctx: VisibilityComputeContext,
  tz: string,
): { patch: VisibilityPatch; adjusted: boolean } {
  const patch: VisibilityPatch = {};

  if (current.showDeletedOnly && !target.isDeleted) {
    patch.showDeletedOnly = false;
  }

  const dateCheck = checkDateDimensionFor(current, target, tz);
  if (!dateCheck.inside && dateCheck.patch) {
    Object.assign(patch, dateCheck.patch);
  }

  if (!statusInActiveTab(current.statusFilter, target)) {
    patch.statusFilter = [...ALL_STATUS_KEYS];
  }

  const filtersResult = computeFiltersPatch(current.filters, target, ctx);
  if (filtersResult.changed) {
    patch.filters = filtersResult.next;
  }

  const q = current.searchQuery.trim().toLowerCase();
  if (q.length > 0 && !target.partnerName.toLowerCase().includes(q)) {
    patch.searchQuery = "";
  }

  return { patch, adjusted: Object.keys(patch).length > 0 };
}

/**
 * Date-only visibility check: does the target's relevant date fall inside the
 * active recap window? Used by the chat-panel banner to distinguish a true
 * date mismatch from a status/filter-only mismatch.
 */
export function isPreOrderDateInRange(
  current: RecapStateView,
  target: PreOrderTarget,
  tz: string,
): boolean {
  return checkDateDimensionFor(current, target, tz).inside;
}

/**
 * Convenience helper for tests: returns a default RecapStateView with neutral
 * filters. Real callers should pass the live state.
 */
export function emptyRecapStateView(
  overrides: Partial<RecapStateView> = {},
): RecapStateView {
  return {
    recapMode: "order",
    rangeMode: "exact",
    selectedDate: startOfDay(new Date("2026-04-30T00:00:00")),
    endDate: undefined,
    startDateTime: undefined,
    endDateTime: undefined,
    statusFilter: [...ALL_STATUS_KEYS],
    searchQuery: "",
    filters: {
      sources: [],
      employees: [],
      locations: [],
      myPartnersOnly: false,
      clientGroupIds: [],
    },
    showDeletedOnly: false,
    ...overrides,
  };
}
